import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import { Plus, PlusCircle, Search, SearchIcon } from "lucide-react";
import CommonTable from "../CommonTable";
import SearchFilterButtons from "../SearchFilterButtons";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import CreateLab from "./CreateLab";
import AddPatient from "../SearchPatient/AddPatient";
import Swal from "sweetalert2";
import { useLoader } from "../../_helper/LoaderContext";
import { useNavigate } from "react-router";
import { deletePatient, getPatientList, statusPatient } from "./service/patient-service";
import { toast } from "react-toastify";
import { stateMaster } from "../LabProfile/service/profile-service";


const PatinetHome = () => {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const [users, setUsers] = useState([]);

    const [data, setData] = useState({})
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const { setLoading } = useLoader();
    const navigate = useNavigate();
    const [gender, setGender] = useState("all");
    const [appliedGender, setAppliedGender] = useState("all");
    const [states, setStates] = useState("all");

    useEffect(() => {
        getList()
    }, [currentPage, entriesPerPage])

    const getList = async (query = appliedSearch, selectedGender = appliedGender) => {
        setLoading(true)
        try {
            const form_data = new FormData()
            form_data.append("search", query);
            form_data.append("page", currentPage);
            form_data.append("limit", entriesPerPage);
            form_data.append("gender", selectedGender);
            const res = await getPatientList(form_data)
            // console.log(res)
            if (res.data.success == true) {
                const filterData = res.data.data.patients.map((item) => ({
                    uniqueId: item.uniqueId,
                    name: item.fname,
                    age: item.age,
                    gender: item.gender,
                    email: item.email,
                    contact: item.contact,
                    isActive: item.isActive,
                    _id: item._id,
                    address: item.address,
                    state: item.state,
                    profilePicture: item.profilePicture
                }))
                setUsers(filterData)
                setTotalEntries(res.data.data.pagination.total || 0);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const columns = [
        { header: t("Patient ID"), accessor: "uniqueId", width: '80px' },
        { header: t("Name"), accessor: "name", width: '180px' },
        { header: t("Email"), accessor: "email", width: '200px' },
        {
            header: t("Contact"),
            accessor: "contact",
            width: '150px',
            render: (value) => {
                if (!value) return "-";
                // Add + sign and format cleanly
                return <strong>+{value}</strong>;
            }
        },
        { header: t("Age"), accessor: "age", width: '50px' },
        { header: t("Gender"), accessor: "gender", width: '100px' },
    ];

    const handleClose = () => {
        setShow(false);
    }

    const handleToggleStatus = (data) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: data.isActive
                ? t("You want to inactivate this patient?")
                : t("You want to activate this patient?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: `${data.isActive == true ? t("Inactive") : t("Active")}`
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true)
                try {
                    const res = await statusPatient(data._id)
                    if (res.data.success == true) {
                        toast.success(res.data.message)
                        getList()
                    }
                } catch (error) {
                    console.log(error)
                } finally {
                    setLoading(false)
                }
            }
        });
    };

    const totalPages = Math.ceil(totalEntries / entriesPerPage);

    const actions = [
        // { label: "View", onClick: (row) => handleEdit(row) },
        { label: "Edit", onClick: (row) => handleEdit(row) },
        { label: "Delete", onClick: (row) => detele(row) },
    ];

    const handleEdit = (user) => {
        // alert(`Edit user: ${user.name}`);
        setShow(true)
        setData(user)
    };

    const handleShow = () => {
        setData({})
        setShow(true);
    }

    const detele = (data) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: t("You want to delete this patient?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t("Yes, Delete It!")
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true)
                try {
                    const res = await deletePatient(data._id)
                    if (res.data.success == true) {
                        toast.success(res.data.message)
                        getList()
                    }
                } catch (error) {
                    console.log(error)
                } finally {
                    setLoading(false)
                }
            }
        });
    };

    const handleSearch = () => {
        const query = search.trim();
        setAppliedSearch(query);
        setAppliedGender(gender);
        if (currentPage === 1) {
            getList(query, gender);
        } else {
            setCurrentPage(1);
        }
    };

    const handleClear = () => {
        setSearch("");
        setGender("all");
        const hadFilter = Boolean(appliedSearch) || appliedGender !== "all";
        setAppliedSearch("");
        setAppliedGender("all");
        if (!hadFilter) return;
        if (currentPage === 1) {
            getList("", "all");
        } else {
            setCurrentPage(1);
        }
    };

    useEffect(() => {
        fetchStates()
    }, [])

    const fetchStates = async () => {
        setLoading(true)
        try {
            const res = await stateMaster({ country: "India" });
            if (res.data.success == true) {
                setStates(res.data.data);
                // setStatesLoaded(true);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    };

    return (
        <>
            <Card>
                <div className="p-3 mt-4">
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                        <div className="input-group" style={{ flex: "1 1 240px", maxWidth: 420 }}>
                            <span className="input-group-text tw-bg-white" id="basic-addon1"><SearchIcon /></span>
                            <input
                                type="text"
                                className="form-control icon_form"
                                placeholder={t("Search by Patient Id / Name / Email / Phone")}
                                aria-label={t("Search by Patient Id / Name / Email / Phone")}
                                aria-describedby="basic-addon1"
                                style={{ borderLeft: 'none' }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                            />
                        </div>
                        <select
                            className="form-select"
                            aria-label={t("Filter by gender")}
                            style={{ flex: "0 1 160px", maxWidth: 160, height: 38 }}
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                        >
                            <option value="all">{t("ALL")}</option>
                            <option value="male">{t("Male")}</option>
                            <option value="female">{t("Female")}</option>
                            <option value="other">{t("Other")}</option>
                        </select>
                        <SearchFilterButtons onSearch={handleSearch} onClear={handleClear} />
                        <div className="ms-md-auto">
                            <Button variant="primary" onClick={handleShow}>
                                {t("Add")}
                            </Button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <CommonTable
                                columns={columns}
                                data={users}
                                onToggleStatus={handleToggleStatus}
                                onAction={actions}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                entriesPerPage={entriesPerPage}   // pass value
                                setEntriesPerPage={setEntriesPerPage}  // pass setter
                            />
                        </div>
                    </div>
                </div>
            </Card>
            <Modal show={show} onHide={handleClose}>
                <AddPatient setUploadModelOpen={handleClose} toggleUploadModel={handleClose} data={data} getList={getList} />
            </Modal>

        </>
    );
};

export default PatinetHome;
